<template>
  <hr-records-section
    :selecting="selecting"
    :state="state"
    :standalone="standalone"
    :floating-create="floatsCreate"
    :create-takes-selection="!!create?.takesRecords"
    :export-label="exportLabel"
    :export-waiting="isRestoringView"
    @export="onExport"
  >
    <!--
      One control. Sibling creation paths — import, a bulk invite, a copy of what is selected —
      belong in its menu rather than beside it, so the reader makes one decision however many routes
      there are into it. The split button appears only when there is something behind the chevron.
    -->
    <template
      v-if="create"
      #create
    >
      <!--
        Floating at a phone width, where a header that scrolls away takes the screen's one primary
        action with it. Only when this table is the screen: a control pinned to the window from
        inside a dialog floats free of the surface that owns it.
      -->
      <hr-page-sticky
        v-if="floatsCreate"
        class="records-table__fab-anchor"
        position="bottom-right"
        :offset="FAB_OFFSET"
      >
        <hr-page-action-fab
          :icon="createIcon"
          :label="createLabel"
          :disable="create.disable || !!create.unavailableReason || isCreateBusy || hasNothingToTake"
          :data-e2e="create.dataE2e"
          @click="onCreatePressed"
        >
          <hr-adaptive-menu
            v-if="compactCreatePaths.length > 1"
            v-model="isCreateMenuOpen"
            :title="createLabel"
          >
            <records-create-menu
              :lead="compactCreatePaths[0]"
              :heading="create.menuHeading"
              :paths="compactCreatePaths.slice(1)"
              :count="rows.length"
            />
          </hr-adaptive-menu>
        </hr-page-action-fab>
      </hr-page-sticky>
      <hr-split-btn
        v-else-if="createMenu.length"
        variant="filled"
        :legacy-props="LEGACY_CREATE_PROPS"
        :label="createLabel"
        :disable="create.disable || !!create.unavailableReason || hasNothingToTake"
        :loading="isCreateBusy"
        :data-e2e="create.dataE2e"
        @click="runPrimaryAction"
      >
        <records-create-menu
          :heading="create.menuHeading"
          :paths="createMenu"
          :count="rows.length"
        />
      </hr-split-btn>
      <hr-page-action-btn
        v-else
        :label="createLabel"
        :disable="create.disable || hasNothingToTake"
        :loading="isCreateBusy"
        :unavailable="!!create.unavailableReason"
        :data-e2e="create.dataE2e"
        @click="runPrimaryAction"
      >
        <hr-tooltip v-if="create.unavailableReason">
          {{ create.unavailableReason }}
        </hr-tooltip>
      </hr-page-action-btn>
    </template>

    <!--
      One menu rather than a control each: these are rare, administrative and mostly irreversible,
      and spread across the header they would read as things the reader is meant to do.
    -->
    <template
      v-if="manageOptions.length"
      #manage
    >
      <hr-overflow-menu-btn
        :options="manageOptions"
        :aria-label="$t('common.tableManageCollection')"
        data-e2e="button-manage-collection"
      />
    </template>

    <template
      v-if="configure"
      #configure
    >
      <!--
        Waiting while what it opens is still being read, rather than disabled: a greyed control says
        the reader may not change these settings, which is a different thing from not yet.
      -->
      <hr-icon-action-btn
        icon="settings"
        :aria-label="actionLabel(configure, rows.length)"
        :disable="configure.disable"
        :loading="configure.loading"
        :data-e2e="configure.dataE2e"
        @click="configure.handler"
      >
        <hr-tooltip>{{ actionLabel(configure, rows.length) }}</hr-tooltip>
      </hr-icon-action-btn>
    </template>

    <template
      v-if="description"
      #description
    >
      <hr-p :text="description" />
    </template>

    <template
      v-if="title"
      #title
    >
      <records-view-title
        v-if="showsViewSelector"
        :title="title"
        :context="`${saveFilterId}Views`"
        :filters="filters"
        :pagination="currentPagination"
        :columns="selectedColumns"
        :other-settings="otherSettings"
        :filter-callbacks="columns"
        :active-view-id="activeViewId"
        :default-state="defaultState"
        :default-view-label="defaultViewLabel"
        :restore-state="restoredViewState"
        @load-view="onLoadView"
        @update:active-view-id="$emit('update:activeViewId', $event)"
        @update:view-state="onViewStateChange"
        @view-unavailable="onViewUnavailable"
        @restore-settled="onRestoreSettled"
      />
      <!-- The page's own heading when this table is the screen; a section's inside anything else. -->
      <component
        :is="standalone ? 'h1' : 'h2'"
        v-else
        class="records-table__title"
      >
        {{ title }}
      </component>
    </template>

    <template
      v-if="!disableSearch"
      #search
    >
      <hr-search-bar
        v-model="searchTerm"
        :placeholder="searchPlaceholder ?? $t('common.tableSearch')"
        data-e2e="input-search-table"
      />
    </template>

    <template #view>
      <hr-icon-action-btn
        v-for="control in views"
        :key="control.key"
        :icon="control.icon"
        :aria-label="actionLabel(control, rows.length)"
        :disable="control.disable"
        :data-e2e="control.dataE2e"
        @click="control.handler"
      >
        <hr-tooltip>{{ actionLabel(control, rows.length) }}</hr-tooltip>
      </hr-icon-action-btn>
    </template>

    <template
      v-if="showsFilterBar"
      #filters
    >
      <records-filter-bar
        :filters="filters"
        :filter-criteria="filterCriteria"
        :filter-groups="columnGroupLabels"
        @update:filters="onFiltersChanged"
      />
    </template>

    <template
      v-if="$slots.selection || picker"
      #selection
    >
      <!--
        Drawn from the selection, not the rows on screen, so a person the filters, the search or the
        view hide can still be seen and removed.
      -->
      <div
        v-if="picker"
        class="records-table__picked"
      >
        <hr-input-chip
          v-for="row in selected"
          :key="row[rowKey]"
          :label="rowLabel(row)"
          :hide-remove="isRowDisabled(row)"
          :remove-aria-label="$t('common.filter.buttonRemoveSelectionLabel', { name: rowLabel(row) })"
          @remove="onRemovePicked(row)"
        />
      </div>
      <slot
        v-else
        name="selection"
        :selected="selected"
        :clear="clearSelection"
      />
    </template>
    <!--
      Acts first, then a separator, then the ones that only send records out, so the bar ends on
      the ones that change nothing.
    -->
    <template
      v-if="hasActBar"
      #act
    >
      <hr-supporting-btn
        v-for="action in barActs.acts"
        :key="action.key"
        :label="actionLabel(action, selected.length)"
        :icon="action.icon"
        :disable="action.disable"
        :data-e2e="action.dataE2e"
        @click="action.handler(selected)"
      />
      <hr-divider
        v-if="barActs.acts.length && barActs.extracts.length"
        vertical
      />
      <hr-supporting-btn
        v-for="action in barActs.extracts"
        :key="action.key"
        :label="actionLabel(action, selected.length)"
        :icon="action.icon"
        :disable="action.disable"
        :data-e2e="action.dataE2e"
        @click="action.handler(selected)"
      />
    </template>

    <template #body>
      <hr-data-table
        v-bind="tableAttrs()"
        :rows="filteredRows"
        :columns="tableColumns"
        :row-key="rowKey"
        :pagination="currentPagination"
        :selection="selection"
        :selected="selected"
        :loading="isLoadingRecords"
        :pending="isRestoringView"
        :table-row-class-fn="getRowClass"
        :virtual-scroll="virtualScroll"
        :virtual-scroll-item-size="VIRTUAL_SCROLL_ITEM_SIZE"
        :virtual-scroll-sticky-size-start="VIRTUAL_SCROLL_ITEM_SIZE"
        :table-style="{ maxHeight: tableMaxHeight }"
        @update:pagination="onSetPagination"
        @update:selected="$emit('update:selected', $event)"
      >
        <!--
          The actions column's header is empty by definition — the column has no data to name — so
          it is where the column picker goes, at the end of the header row it governs.
        -->
        <template
          v-if="hasActionsColumn"
          #header-cell-actions="slotProps"
        >
          <hr-th :props="slotProps">
            <slot
              name="header-cell-actions"
              v-bind="slotProps"
            >
              <!--
                Waiting while a view is restored: the columns it edits are the ones the view is
                about to replace, so a set chosen in that window is overwritten the moment it lands.
              -->
              <hr-icon-action-btn
                v-if="showsColumnPicker"
                icon="view_column"
                size="sm"
                :loading="isRestoringView"
                :aria-label="$t('common.tableEditColumns')"
                data-e2e="button-edit-table-columns"
                @click="onSelectColumns"
              >
                <hr-tooltip>{{ $t('common.tableEditColumns') }}</hr-tooltip>
              </hr-icon-action-btn>
            </slot>
          </hr-th>
        </template>
        <!--
          One control per row. A row that can be acted on in more than one way puts every one of
          them in a kebab menu rather than lining the buttons up in the cell: the column is shared
          by every row and pinned over the scrolling ones, so its width is set by whichever row
          offers the most, and a row's actions are read one at a time anyway. A single action stays
          a single button.

          Ordered the way a menu is ordered everywhere else: the primary action first, anything
          destructive last and behind a divider.
        -->
        <template
          v-if="$slots['row-actions']"
          #row-actions="slotProps"
        >
          <slot
            name="row-actions"
            v-bind="slotProps"
            :actions-label="rowActionsLabel(slotProps.row)"
          />
        </template>
        <template
          v-for="name in forwardedCellSlots"
          :key="name"
          #[name]="slotProps"
        >
          <slot
            :name="name"
            v-bind="slotProps"
          />
        </template>
        <!--
          A caller that takes either of these over gets it, and what is written here is the fallback.
          The pair is HrDataTable's, and a table that caps how many rows may be chosen has to be able
          to take the select-all away: it would otherwise offer a control that overshoots the cap in
          one click. Both are offered, not just the one with a caller today, because HrDataTable
          documents them as a pair and taking one over without the other is already a decision the
          caller is warned about.

          Branched rather than written as the slot's fallback content, because an empty override is
          the point: Vue falls back when a slot renders nothing, which would put the select-all back
          exactly where a caller asked for it to be gone.
        -->
        <template
          v-if="$slots['header-selection'] || hasDisabledRows"
          #header-selection="slotProps"
        >
          <slot
            v-if="$slots['header-selection']"
            name="header-selection"
            v-bind="slotProps"
          />
          <hr-checkbox
            v-else
            :model-value="slotProps.selected"
            data-e2e="action-bulk-select-all-rows"
            @update:model-value="onSelectAllRows"
          />
        </template>
        <template
          v-if="$slots['body-selection'] || hasDisabledRows"
          #body-selection="slotProps"
        >
          <slot
            v-if="$slots['body-selection']"
            name="body-selection"
            v-bind="slotProps"
          />
          <universal-tooltip
            v-else
            :show-tooltip="isRowDisabled(slotProps.row) && !!disabledRowTooltip"
            :text="disabledRowTooltip"
          >
            <hr-checkbox
              v-model="slotProps.selected"
              :disable="isRowDisabled(slotProps.row)"
            />
          </universal-tooltip>
        </template>
      </hr-data-table>
    </template>

    <template
      v-if="$slots.empty"
      #empty
    >
      <slot name="empty" />
    </template>

    <template
      v-if="$slots['empty-filtered']"
      #empty-filtered
    >
      <slot
        name="empty-filtered"
        v-bind="emptyFilteredScope"
      />
    </template>
  </hr-records-section>
</template>

<script>
import RecordsCreateMenu from 'components/UI/RecordsCreateMenu.vue';
import RecordsFilterBar from 'components/UI/RecordsFilterBar.vue';
import RecordsViewTitle from 'components/UI/RecordsViewTitle.vue';
import UniversalColumnSelector from 'components/UI/UniversalColumnSelector';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { chain, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import {
  HrAdaptiveMenu,
  HrCheckbox,
  HrDataTable,
  HrDivider,
  HrIconActionBtn,
  HrInputChip,
  HrOverflowMenuBtn,
  HrPageActionBtn,
  HrPageActionFab,
  HrP,
  HrPageSticky,
  HrRecordsSection,
  HrSearchBar,
  HrSplitBtn,
  HrSupportingBtn,
  HrTh,
  HrTooltip,
} from 'src/design-system';
import FiltersMixin from 'src/mixins/FiltersMixin';
import Interactions from 'src/mixins/Interactions';
import TableMixin from 'src/mixins/TableMixin';
import {
  actionLabel, compactPaths, splitActs, validateActs, validateCreate, validateViews,
} from 'components/UI/recordActions';
import {
  clearViewState, readViewState, writeViewState,
} from 'components/UI/recordsViewState';
import { auth } from 'src/store/auth';
import { sortByArray } from 'src/utils/AppUtils';

const VIRTUAL_SCROLL_ITEM_SIZE = 48;

/*
 * What the rows are given to scroll within at a phone width. A bounded scroller is the whole of
 * what makes the column header stick — Quasar sticks it to the element that scrolls, and an element
 * that never scrolls never sticks anything.
 */
const COMPACT_TABLE_MAX_HEIGHT = '70vh';
// Clear of the screen edges, and of the bottom bar on a phone.
const FAB_OFFSET = [16, 16];
/*
 * Shared with the primary where it leads the FAB's menu, but only beside a sibling that has an icon:
 * it is there to line the labels up, and in a menu of bare labels it would push its own out of line.
 */
const CREATE_ICON = 'add';

/*
 * The legacy theme has no filled variant of its own, so the create control would fall back to a
 * plain grey button — the one control on the page that should not. White on primary is what every
 * other primary action wears there.
 * @todo Remove when the legacy theme is removed.
 */
const LEGACY_CREATE_PROPS = { color: 'primary', unelevated: true };

export default {
  name: 'RecordsTable',

  components: {
    RecordsCreateMenu,
    RecordsFilterBar,
    HrAdaptiveMenu,
    HrCheckbox,
    HrDataTable,
    HrDivider,
    HrIconActionBtn,
    HrInputChip,
    HrOverflowMenuBtn,
    HrPageActionBtn,
    HrPageActionFab,
    HrP,
    HrPageSticky,
    HrRecordsSection,
    HrSearchBar,
    HrSplitBtn,
    HrSupportingBtn,
    HrTh,
    HrTooltip,
    RecordsViewTitle,
    UniversalTooltip,
  },

  mixins: [FiltersMixin, Interactions, TableMixin],

  /*
   * The root here is the records section, but QTable's own surface is what a caller is reaching for
   * when they pass `no-data-label`, `no-header` or a table class. Route attrs to the table so this
   * stays a drop-in for FilteredTable, whose root WAS the table, rather than enumerating QTable.
   */
  inheritAttrs: false,

  props: {
    /** Column definitions, in QTable's shape, plus the selection metadata the column picker reads. */
    columns: { type: Array, required: true },
    /** The records. Filtering, searching and sorting all happen over this array. */
    rows: { type: Array, required: true },
    /** Rows that cannot be selected, matched on `rowKey`. */
    disabledRows: { type: Array, default: () => [] },
    /** Tooltip explaining why a disabled row cannot be selected. */
    disabledRowTooltip: { type: String, default: null },
    /** Whether a row opens anything when clicked. Unset means every row does, given a `row-click` listener. */
    rowClickable: { type: Function, default: null },
    /**
     * A record's name as the reader knows it. It names the row's actions, which the `row-actions`
     * slot receives as `actionsLabel`: a menu named for nothing is announced identically on every row.
     */
    rowName: { type: Function, default: null },
    /** Related collections a filter may reach into, keyed by the column's `foreignObject`. */
    foreignObjects: { type: Object, default: () => ({}) },
    /** Column names shown before the user picks their own. Unset means all of them. */
    defaultColumns: { type: Array, default: () => [] },
    /** Filters applied before the user sets their own. */
    defaultFilters: { type: Object, default: () => ({}) },
    /** QTable pagination, two-way bound. */
    pagination: { type: Object, default: () => ({}) },
    /**
     * Key under which this table's saved views, and the reader's state within them, are kept.
     *
     * It takes a `title` as well to do anything: the selector is what writes the state and it lives
     * in the title region, so a titled table persists and a titleless one silently does not.
     */
    saveFilterId: { type: String, default: null },
    /** Row property that identifies a record. */
    rowKey: { type: String, default: 'id' },
    /** Selected rows, two-way bound. */
    selected: { type: Array, default: () => [] },
    /** QTable selection mode. */
    selection: { type: String, default: 'none' },
    /**
     * The checked rows are the value being chosen, such as who is linked to a schedule, rather than
     * the rows an action will run on. Filters and views then only help find records, so changing
     * them leaves the checked rows alone.
     */
    picker: { type: Boolean, default: false },
    /** A picker's name for a checked row, shown on the chip that removes it. Required with `picker`. */
    rowLabel: { type: Function, default: null },
    /** Base name of the exported file, without extension. */
    exportFilename: { type: String, default: 'export' },
    /** Labels for the column groups the picker and the filter menu group by. */
    columnGroupLabels: { type: Array, default: () => [] },
    /**
     * The collection's name, shown at the leading end of the header. With saved views enabled it
     * becomes the trigger for them and gives way to the loaded view's name.
     */
    title: { type: String, default: null },
    /**
     * What this collection is, in a sentence or two. Sits under the title, where it belongs to the
     * records rather than to the page around them.
     */
    description: { type: String, default: null },
    /**
     * The one control that makes more records. Sibling creation paths go in its `menu` rather than
     * beside it as controls of their own, because a ranked menu is one decision for the reader
     * however many routes it holds.
     * @type {import('components/UI/recordActions').RecordAction}
     */
    create: { type: Object, default: null, validator: validateCreate },
    /**
     * What can be done to the selected records, and what can be taken out of them. Every entry
     * declares a `kind` and a `scope`; the model's whole argument for actions being data is that
     * nothing can require those of slot content.
     * @type {import('components/UI/recordActions').RecordAction[]}
     */
    acts: { type: Array, default: () => [], validator: validateActs },
    /**
     * Controls that change only what is on screen. Not actions, so they persist through selection.
     * @type {import('components/UI/recordActions').RecordAction[]}
     */
    views: { type: Array, default: () => [], validator: validateViews },
    /**
     * Settings that change the shape of records made from here on. One control.
     * @type {import('components/UI/recordActions').RecordAction}
     */
    configure: { type: Object, default: null },
    /**
     * This table is the screen's main content rather than part of something else, so its compact
     * containers may anchor to the viewport. A dialog leaves it off.
     */
    standalone: { type: Boolean, default: false },
    /** The records are being fetched. */
    loading: { type: Boolean, default: false },
    /** Hide the saved-view selector. */
    disableViews: { type: Boolean, default: false },
    /** Hide the export menu. */
    disableExport: { type: Boolean, default: false },
    /** Hide the search field. */
    disableSearch: { type: Boolean, default: false },
    /**
     * Hide the column picker. For a table whose columns are not the reader's to choose: a short
     * read-only list, or one inside a dialog, where the choice is gone the moment it closes.
     */
    disableColumnPicker: { type: Boolean, default: false },
    /** Placeholder naming what is searched, e.g. "Search clients". Falls back to a generic one. */
    searchPlaceholder: { type: String, default: null },
    /** Cap on the table's scroll height. */
    maxHeight: { type: String, default: undefined },
    /** Render rows with QTable's virtual scroller. */
    virtualScroll: { type: Boolean, default: false },
    /** Hide the filter row entirely, for a table nothing can be filtered on. */
    hideFilterChips: { type: Boolean, default: false },
    /** Extra state saved into and restored from a table view. */
    otherSettings: { type: Object, default: () => ({}) },
    /** Filters owned by the caller; when set, the table stops managing its own. */
    externalFilters: { type: Object, default: null },
    /** Selected columns owned by the caller; when set, the table stops managing its own. */
    externalSelectedColumns: { type: Array, default: null },
    /** Id of the saved view currently loaded. */
    activeViewId: { type: String, default: null },
    /**
     * What to call the saved-views row that leaves every view behind and shows the collection
     * whole — "All clients". Composed by the caller, which is the only place that knows both the
     * collection's noun and how its language builds the phrase.
     */
    defaultViewLabel: { type: String, default: null },
  },

  /*
   * `row-click` is deliberately not declared. Undeclared, a caller's listener stays in $attrs and
   * reaches the table, and only when there is one: QTable marks every row as clickable whenever it
   * has a row-click listener, so re-emitting it unconditionally put a pointer on rows that do
   * nothing when clicked.
   */
  emits: [
    'update:pagination',
    'update:selected',
    'update:selectedColumns',
    'update:activeViewId',
    'exported',
    'load-view',
  ],

  data() {
    return {
      VIRTUAL_SCROLL_ITEM_SIZE,
      searchTerm: '',
      FAB_OFFSET,
      CREATE_ICON,
      LEGACY_CREATE_PROPS,
      isCreateMenuOpen: false,
      currentPagination: {},
      filters: {},
      selectedColumns: [],
      /*
       * Both captured before the first render, so the saved-view selector has them on mount: it
       * restores what was stored during its own mounted hook.
       */
      restoredViewState: null,
      hasReadStoredState: false,
      defaultState: null,
      /*
       * Known before the first render, which is the whole point: the stored view is read from
       * local storage synchronously, so the section can hold its place rather than show the
       * collection's defaults and replace them once the view comes back from Firestore.
       */
      isRestoringView: false,
      /*
       * Not the same question as isRestoringView, which is true only while a stored view id is
       * read. Stored edits without one are put back too, before the selector settles, and are
       * just as much a restore.
       */
      hasRestoreSettled: false,
      isRunningPrimaryAction: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),

    /*
     * Where this reader's state for this page is kept. Null when the page keeps nothing, or before
     * there is a reader to keep it for — either way nothing is read and nothing is written.
     */
    viewStateOwner() {
      if (this.saveFilterId === null || !this.userDetails?.userId) {
        return null;
      }
      return { userId: this.userDetails.userId, pageId: this.saveFilterId };
    },

    /*
     * Derived once per column change rather than per render. Called from the template it returned a
     * fresh array every time the parent re-rendered, so the chips bar saw a changed prop on each
     * one, and the appointments table builds over a hundred columns.
     */
    filterCriteria() {
      return this.getFilterCriteriaFromColumns(this.columns);
    },

    // The per-cell slots the caller passed, handed straight to HrDataTable's parametric families.
    // `header-cell-actions` is excluded: this component renders that cell itself and offers the
    // caller's slot inside it, so forwarding it as well would bind the same slot twice.
    forwardedCellSlots() {
      return Object.keys(this.$slots)
        .filter((name) => name.startsWith('body-cell-') || name.startsWith('header-cell-'))
        .filter((name) => name !== 'header-cell-actions');
    },

    createMenu() {
      return this.create?.menu ?? [];
    },

    /*
     * A creation path can say it is not a realistic phone workflow — picking a spreadsheet off a
     * device to bulk import is the case in hand — and it then leaves the FAB's menu rather than
     * being offered and failing. Adding a folder or a form is the opposite, and stays.
     *
     * Measured by the device rather than by the width, unlike everything else about the compact
     * treatment. What the path is declaring is that the hardware cannot realistically do it; a
     * narrow window on a desktop still has the file picker, and taking the route away there drops
     * it from the menu for no reason the reader can see.
     *
     * The primary route leads the list, because on a phone pressing the FAB opens the menu rather
     * than running anything: M3 has no split FAB, and a chevron beside a label is two fingertip
     * targets in a control meant to be hit without looking.
     */
    compactCreatePaths() {
      const paths = this.$q.platform.is.mobile ? compactPaths(this.createMenu) : this.createMenu;
      if (!paths.length) {
        return [];
      }
      return [{
        ...this.create,
        key: `${this.create.key}-primary`,
        label: this.create.menuLabel ?? this.create.label,
        icon: paths.some((path) => path.icon) ? this.createIcon : null,
        menu: undefined,
        handler: this.runPrimaryAction,
      }, ...paths];
    },

    /*
     * Bounded at a phone width, and left alone above it. Sticking the column names is worth a second
     * scrolling region only where the rows fill the screen several times over and the names have
     * gone long before the reader stops. A wide screen has room to show the names and the rows
     * together, so the nested scroller would cost something and buy nothing.
     *
     * A caller that asked for a height gets it at every width: that is an answer to a different
     * question, and not one this should overrule.
     */
    tableMaxHeight() {
      if (this.maxHeight !== undefined) {
        return this.maxHeight;
      }
      return this.isCompact ? COMPACT_TABLE_MAX_HEIGHT : undefined;
    },

    // One boundary for the whole compact treatment, so nothing changes its mind halfway across it.
    isCompact() {
      return this.$q.screen.lt.sm;
    },

    /*
     * A FAB at a phone width, a button in the page header from the small breakpoint up: by then
     * there is a header worth putting the primary action in, and a control floating over the
     * records is covering records there is room to show.
     */
    floatsCreate() {
      return this.standalone && this.isCompact;
    },

    barActs() {
      return splitActs(this.acts);
    },

    hasActBar() {
      const { acts, extracts } = this.barActs;
      return acts.length + extracts.length > 0;
    },

    /*
     * Handed nothing, unlike the bar's actions. These are about the collection rather than about
     * anything picked out of it, so there is no selection to pass and passing the rows on screen
     * would be a different claim: what they act on is everything, filtered out of view or not.
     */
    manageOptions() {
      return this.barActs.manage.map((action) => ({
        label: actionLabel(action, 0),
        icon: action.icon,
        disable: action.disable,
        loading: action.loading,
        destructive: action.destructive,
        action: () => action.handler(),
      }));
    },

    /*
     * Null when there is nothing to export or nothing that could be: the section takes the control
     * away on selection itself, so this only has to answer whether the collection exports at all.
     */
    exportLabel() {
      if (this.disableExport) {
        return null;
      }
      /*
       * Named without a count while the stored view is still on its way, and ahead of the count
       * being read at all: the records in hand are the ones the view is about to replace, so a
       * number taken from them is wrong, and none of them being in yet is not an empty collection.
       * The section keeps the control in the row and makes it inert.
       */
      if (this.isRestoringView) {
        return this.$t('common.tableExport');
      }
      if (this.filteredRows.length === 0) {
        return null;
      }
      return this.$tc('common.tableExportAll', this.filteredRows.length, {
        count: this.filteredRows.length,
      });
    },

    hasSearchTerm() {
      return (this.searchTerm ?? '').trim() !== '';
    },

    // Only the FAB shows it. A page header labelled in words has no use for a "+" in front of them.
    createIcon() {
      return this.create?.icon ?? CREATE_ICON;
    },

    /*
     * An action that takes the records says how many it will take, counted the way it takes them:
     * from what is showing, not from everything loaded. Without a count while the count is not one
     * it could keep, for the reasons the table's own export gives.
     */
    createLabel() {
      if (!this.create.takesRecords) {
        return actionLabel(this.create, this.rows.length);
      }
      if (this.selecting) {
        return actionLabel(this.create, this.selected.length);
      }
      if (this.isLoadingRecords) {
        return actionLabel(this.create, null);
      }
      return actionLabel(this.create, this.filteredRows.length);
    },

    // Waiting on a restore as well as on its own run, for the same reason the export waits.
    isCreateBusy() {
      return !!this.create?.takesRecords && (this.isLoadingRecords || this.isRunningPrimaryAction);
    },

    // The table's own export leaves when nothing is showing; this one stays, since it is the page's.
    hasNothingToTake() {
      return !!this.create?.takesRecords && !this.isLoadingRecords && this.rowsToTake.length === 0;
    },

    /*
     * The checked rows when there are any, and what is showing otherwise. A selection is the reader
     * pointing at rows, so neither a search nor the filters stand between them and what is taken.
     */
    rowsToTake() {
      return this.selecting ? this.selected : this.filteredRows;
    },

    // HrDataTable appends the sticky actions column only when there are row actions to put in it.
    /*
     * The trailing column is the controls column rather than the row-actions column: it holds the
     * picker above the columns it governs whether or not a row can be acted on. Without this the
     * same control had two homes — that header on a table with row actions, the toolbar on one
     * without — and which one a reader got turned on something they could not see.
     */
    hasActionsColumn() {
      return !!this.$slots['row-actions'] || this.showsColumnPicker;
    },

    /*
     * Turned off by the caller, or pointless because there is nothing to choose between. A lone
     * pickable column can only be taken away, which leaves rows with nothing in them.
     */
    showsColumnPicker() {
      if (this.disableColumnPicker) {
        return false;
      }
      return this.columns.filter((column) => !column.hideFromSelection).length > 1;
    },

    /*
     * No column declares itself filterable, so the chip leads to a dialog that can only say "no
     * matching filters". The row goes with it: regions here are absent rather than empty, and a
     * control that opens onto nothing is worse than no control.
     *
     * Still shown when something is already filtered, which can only happen through a default or a
     * saved view. Hiding the bar then would leave the reader looking at a narrowed collection with
     * nothing on screen saying so, and no way to widen it again.
     */
    showsFilterBar() {
      if (this.hideFilterChips) {
        return false;
      }
      return this.filterCriteria.length > 0 || Object.keys(this.filters ?? {}).length > 0;
    },

    // Its own computed so that typing in the search repeats only the text match, not this pass.
    rowsMatchingFilters() {
      return this.getFilteredElementsWithObjects(this.rows, this.filters, this.foreignObjects);
    },

    /*
     * The search narrows within the filters rather than replacing them. FilteredTable searched the
     * whole collection, which left the filter chips on screen describing rows that were not.
     */
    filteredRows() {
      if (!this.hasSearchTerm) {
        return this.rowsMatchingFilters;
      }
      const formattedSearch = this.searchTerm.toLowerCase().trim();

      return this.rowsMatchingFilters.filter((row) => this.visibleColumns.some((column) => {
        const formatFunc = column.format || ((value) => value);
        // Resolved as QTable resolves the cell, so a search matches what is on screen.
        const value = typeof column.field === 'function' ? column.field(row) : row[column.field];
        return String(formatFunc(value, row)).toLowerCase().includes(formattedSearch);
      }));
    },

    /*
     * Every column when the caller named none. Both the first render and a reset to the default
     * view ask this same question, and answering it as "none of them" in either place renders a
     * table with rows and no columns at all, which reads as records that failed to load.
     */
    resolvedDefaultColumns() {
      return this.defaultColumns?.length
        ? this.defaultColumns
        : this.columns.map((column) => column.name);
    },

    visibleColumns() {
      return this.columns
        .filter((column) => this.selectedColumns.includes(column.name) || column.persistent)
        .sort(sortByArray({ arrayToSortBy: this.selectedColumns, searchKey: 'name' }));
    },

    /*
     * QTable puts the pointer and the hover on the row and this Quasar has no per-row class hook, so
     * a row that opens nothing takes both back in each of its cells instead.
     */
    tableColumns() {
      if (!this.rowClickable) {
        return this.visibleColumns;
      }
      return this.visibleColumns.map((column) => ({
        ...column,
        classes: (row) => {
          const classes = typeof column.classes === 'function' ? column.classes(row) : column.classes ?? '';
          return this.isRowClickable(row) ? classes : `${classes} records-table__inert-cell q-td--no-hover`;
        },
      }));
    },

    selecting() {
      return this.selection !== 'none' && this.selected.length > 0;
    },

    // Views persist under saveFilterId, so without one there is nowhere to keep them.
    hasViews() {
      return !this.disableViews && this.saveFilterId !== null;
    },

    /*
     * The selector lives in the title region, so a table with no title has no selector however much
     * it stores. Named rather than spelled out at the two places that ask, because the second is
     * whether to hold the page for a restore, and a restore nothing is there to run never ends.
     */
    showsViewSelector() {
      return this.hasViews && this.title !== null;
    },

    /*
     * A restore is a load like any other as far as the records are concerned: the view decides
     * which of them to show, so none of them is final until it lands. Keeping it separate gave the
     * one table with a saved view loaded a different loading treatment from every other table.
     */
    isLoadingRecords() {
      return this.loading || this.isRestoringView;
    },

    /*
     * Taking over the selection slots costs the header its indeterminate state, which HrDataTable
     * documents, so it only happens for a table that actually has rows nobody may select. Everyone
     * else keeps QTable's own checkboxes and the partial-selection tick with them.
     */
    hasDisabledRows() {
      return this.disabledRows.length > 0;
    },

    /*
     * Two different problems, so two different states. Nothing here yet is answered by filling the
     * collection; nothing matching is answered by stopping hiding what is in it. Telling a reader
     * with four hundred records to add their first one, because they mistyped a search, is what
     * conflating them looks like.
     *
     * Each is only ever reported when the caller supplied the slot for it. Without that guard the
     * section swaps the table out for nothing at all — the table's own no-data row is the fallback
     * for a caller that has said nothing about either.
     */
    state() {
      /*
       * Ahead of the empty states, which it would otherwise lose to: the records are not in yet
       * either, and inviting somebody to add their first one while their saved view is still on
       * its way is the same mistake as the filtered/unfiltered one below, a step earlier.
       */
      /*
       * An empty collection is not knowable until the records are in, and reporting it swaps the
       * table out for the slot — so a clinic with four hundred clients would be invited to add its
       * first one on every cold load. A view still being restored counts as not in yet.
       */
      if (this.$slots.empty && this.rows.length === 0 && !this.isLoadingRecords) {
        return 'empty';
      }
      const isFilteredEmpty = this.rows.length > 0 && this.filteredRows.length === 0;
      return (this.$slots['empty-filtered'] && isFilteredEmpty) ? 'empty-filtered' : 'ready';
    },

    /*
     * What hid the records, and how to stop. The caller writes the words and chooses which control
     * to offer, but neither the search term nor the filters are its to clear: both live here.
     */
    emptyFilteredScope() {
      return {
        hasSearchTerm: this.hasSearchTerm,
        hasFilters: Object.keys(this.filters ?? {}).length > 0,
        clearSearch: () => { this.searchTerm = ''; },
        clearFilters: () => this.onFiltersChanged({}),
      };
    },
  },

  watch: {
    pagination: {
      handler() {
        this.currentPagination = this.pagination;
      },
      immediate: true,
      deep: true,
    },

    externalFilters: {
      handler(newVal) {
        if (newVal !== null) {
          this.filters = newVal;
        }
      },
      deep: true,
      immediate: true,
    },

    externalSelectedColumns: {
      handler(newVal) {
        if (newVal !== null) {
          this.selectedColumns = newVal;
        }
      },
      deep: true,
      immediate: true,
    },

    /*
     * Immediate, so the read still happens before the first render in the ordinary case — but a
     * watcher rather than a line in created(), because the owner is only known once the signed-in
     * user is, and that can land after this table was built. The read has to be what opens the
     * writing: until it has happened there is nothing to compare against, and writing anyway puts
     * the collection's defaults over a stored record nobody has looked at. Not reading costs the
     * reader one restore; writing first costs them the view.
     */
    viewStateOwner: {
      handler(owner) {
        if (owner === null || this.hasReadStoredState) {
          return;
        }
        this.restoredViewState = readViewState(owner.userId, owner.pageId);
        this.hasReadStoredState = true;
      },
      immediate: true,
    },
  },

  /*
   * Before the first render rather than on mount: the saved-view selector puts the stored state
   * back during its own mounted hook, which runs first, and defaults installed after that would
   * wipe what it had just restored.
   */
  created() {
    this.loadSettings();
    this.defaultState = {
      columns: this.resolvedDefaultColumns,
      filters: this.defaultFilters ?? {},
      otherSettings: this.otherSettings ?? {},
      rowsPerPage: this.pagination?.rowsPerPage ?? null,
    };
    /*
     * Only a stored view id is worth waiting for. Stored edits without one are put back from local
     * storage alone, with nothing to read and nothing to wait for, and holding the place for them
     * would be a placeholder shown in front of records that were already right.
     *
     * Gated on the selector being on screen, not on a stored id alone: it is what runs the restore
     * and reports it over, so without it the page would hold its place until the reader reloaded.
     */
    this.isRestoringView = this.showsViewSelector
      && (this.restoredViewState?.viewId ?? null) !== null;
  },

  methods: {
    actionLabel,

    rowActionsLabel(row) {
      return this.rowName ? this.$t('common.labelRowActions', { name: this.rowName(row) }) : null;
    },

    // With nothing else on offer here the FAB is the action; with siblings it is the way into them.
    onCreatePressed() {
      if (this.compactCreatePaths.length > 1) {
        this.isCreateMenuOpen = !this.isCreateMenuOpen;
        return;
      }
      this.runPrimaryAction();
    },

    /*
     * The records on screen, handed to the action so it can act on them. A collection that makes
     * nothing has no use for them and ignores the argument; one whose primary action is to take the
     * records somewhere cannot ask the question itself, because which rows are showing and which
     * columns are chosen are both the table's to know.
     */
    async runPrimaryAction() {
      const columns = this.visibleColumns.filter((column) => !column.preventExport);
      if (!this.create.takesRecords) {
        this.create.handler({ rows: this.filteredRows, columns });
        return;
      }

      /*
       * Billing stamps what it is handed, so each of these would record the wrong rows as sent: a
       * set still loading or about to be replaced by a restore, or a second copy of the same set.
       */
      if (this.isLoadingRecords || this.isRunningPrimaryAction || this.hasNothingToTake) {
        return;
      }

      this.isRunningPrimaryAction = true;
      try {
        await this.create.handler({ rows: [...this.rowsToTake], columns });
      } finally {
        this.isRunningPrimaryAction = false;
      }
    },

    onSelectAllRows() {
      const selectedWithoutDisabled = this.selected.filter((row) => !this.isRowDisabled(row));
      const selectedDisabledOnly = this.selected.filter((row) => this.isRowDisabled(row));

      if (selectedWithoutDisabled.length === 0) {
        const allRows = this.filteredRows
          .filter((row) => !this.isRowDisabled(row))
          .concat(selectedDisabledOnly);
        this.$emit('update:selected', allRows);
        return;
      }
      this.clearSelection();
    },

    onSetPagination(pagination) {
      this.currentPagination = pagination;
      this.$emit('update:pagination', this.currentPagination);
    },

    // Rows checked for an action may be ones the new filters hide, and the action would still take them.
    onFiltersChanged(filters) {
      if (!this.picker) {
        this.clearSelection();
      }
      this.filters = filters;
    },

    onSelectColumns() {
      const selectionColumns = chain(this.columns)
        .reject({ hideFromSelection: true })
        .map((column) => ({
          value: column.name,
          label: column.selectionLabel || column.label || column.exportLabel || '',
          disable: column.mandatory || false,
          group: column.group || null,
        }))
        .value();

      this.openComponentDialog(
        UniversalColumnSelector,
        {
          options: selectionColumns,
          optionGroupLabels: this.columnGroupLabels,
          selected: this.selectedColumns,
          title: this.$t('common.tableEditColumns'),
          isAbleToReorder: true,
        },
        (newSelectedColumns) => {
          this.selectedColumns = newSelectedColumns;
          this.saveSelectedColumns();
        },
      );
    },

    onRestoreSettled() {
      this.isRestoringView = false;
      this.hasRestoreSettled = true;
    },

    /*
     * Loading a view is asking for the rows it defines. A search left over from before would quietly
     * hide some of them, under a view that claims to show them all.
     */
    onLoadView(tableViewProps) {
      this.searchTerm = '';
      this.selectedColumns = tableViewProps.columns;
      this.onSetPagination(tableViewProps.pagination);
      /*
       * A view the reader picks is a filter change. A restore is not: it puts back where the reader
       * was, and a table opened with rows already checked would quietly drop them.
       */
      if (this.hasRestoreSettled) {
        this.onFiltersChanged(tableViewProps.filters);
      } else {
        this.filters = tableViewProps.filters;
      }
      this.$emit('load-view', tableViewProps);
    },

    /*
     * The current view, serialized: every filtered row and the columns on screen, as one format.
     * The scope is not a choice — a subset picked by eye is a filter, a tag or a saved view — and
     * a format menu only ever asked the reader a question they had no basis to answer.
     */
    async onExport() {
      /*
       * Nothing to write yet: the records in hand are the ones the view is about to replace, so a
       * file taken now holds a set nobody asked for. The control says as much by waiting, and this
       * is the second lock on the same door.
       */
      if (this.isRestoringView) {
        return;
      }

      this.$q.loading.show();
      try {
        /*
         * The columns on screen, which is what this control says it exports. Selecting by the
         * reader's chosen set alone dropped any column a table pins as persistent: those show
         * without being chosen, so they were visible in the table and missing from the file.
         */
        const columnsToExport = this.visibleColumns.filter((column) => !column.preventExport);
        /*
         * Held still across the write. The rows can change under an awaited write — a bound store
         * updating, a filter cleared — and a listener told about a different set from the one in
         * the file would record records that never went out.
         */
        const exportedRows = [...this.filteredRows];
        await this.exportTableDataToSpreadsheet(
          exportedRows,
          columnsToExport,
          `${this.exportFilename}.xlsx`,
        );
        /*
         * After the file is written, never before. A listener records that these rows went out, and
         * billing marks them exported on the strength of it — so an export that threw must leave
         * nothing stamped, or rows are recorded as sent in a file nobody received.
         */
        this.$emit('exported', { rows: exportedRows });
      } finally {
        this.$q.loading.hide();
      }
    },

    /*
     * The collection as it stands before anyone has chosen anything. What the reader had last time
     * arrives separately, through the saved-view selector, which is the one thing that knows
     * whether a stored working state is a view, an edit of one, or an edit of this.
     */
    loadSettings() {
      if (this.externalSelectedColumns === null) {
        this.selectedColumns = this.resolvedDefaultColumns;
      }
      if (this.externalFilters === null) {
        this.filters = isEmpty(this.defaultFilters) ? {} : this.defaultFilters;
      }
    },

    saveSelectedColumns() {
      this.$emit('update:selectedColumns', this.selectedColumns);
    },

    /*
     * One record per reader per page, holding the view being read and only what was changed about
     * it. Written on every change, so a reader who closes the tab mid-edit still comes back to it.
     */
    onViewStateChange(record) {
      if (this.viewStateOwner === null || !this.hasReadStoredState) {
        return;
      }
      const { userId, pageId } = this.viewStateOwner;
      writeViewState(userId, pageId, record);
    },

    /*
     * The stored view has been deleted, or this reader may no longer see it. Its edits describe a
     * thing that is gone, so they go with it and the collection's own defaults come back.
     */
    onViewUnavailable() {
      if (this.viewStateOwner !== null) {
        const { userId, pageId } = this.viewStateOwner;
        clearViewState(userId, pageId);
      }
      this.loadSettings();
      this.notifyInfo(this.$t('tableViews.notifyViewUnavailable'));
    },

    onRemovePicked(row) {
      this.$emit('update:selected', this.selected.filter((picked) => picked[this.rowKey] !== row[this.rowKey]));
    },

    clearSelection() {
      this.$emit('update:selected', this.selected.filter((row) => this.isRowDisabled(row)));
    },

    isRowDisabled(row) {
      if (this.disabledRows.length === 0) {
        return false;
      }
      return this.disabledRows.some((disabledRow) => disabledRow[this.rowKey] === row[this.rowKey]);
    },

    isRowClickable(row) {
      return !this.rowClickable || this.rowClickable(row);
    },

    /*
     * A method rather than a computed: $attrs is not reactive, so a computed over it would keep the
     * first listener it saw.
     */
    tableAttrs() {
      const { onRowClick, ...attrs } = this.$attrs;
      if (!onRowClick) {
        return attrs;
      }
      return {
        ...attrs,
        onRowClick: (evt, row, index) => {
          if (this.isRowClickable(row)) {
            onRowClick(evt, row, index);
          }
        },
      };
    },

    getRowClass(row) {
      return this.isRowDisabled(row) ? 'cursor-not-allowed disabled' : '';
    },
  },
};
</script>

<style lang="scss" scoped>
/* One line that scrolls rather than wraps, so checking another person never moves the rows. */
.records-table__picked {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  overflow-x: auto;
}

.records-table__picked > * {
  flex: none;
}

:deep(.records-table__inert-cell) {
  cursor: auto;
}

/*
 * The anchor is what is positioned, so it is what carries the paint order: the FAB inside it is
 * static and a z-index on it would do nothing. Without this the table's own sticky cells, which
 * come later in the document, paint over a control that is meant to float above everything.
 */
.records-table__fab-anchor {
  z-index: var(--app-z-floating);
}

// Matches the type role RecordsViewTitle carries, so a collection without saved views reads the same.
.records-table__title {
  margin: 0;
  font-size: var(--app-type-headline-small-size);
  line-height: var(--app-type-headline-small-line-height);
  font-weight: var(--app-type-headline-small-weight);
  letter-spacing: var(--app-type-headline-small-tracking);
}
</style>
